import React, { useState, useEffect } from 'react';
import { Smartphone, Edit2, Trash2, Plus, Clock, Search, Signal, AlertTriangle, Sparkles } from 'lucide-react';
import { traqPackAPI, Device } from '../lib/supabase';
import EditDeviceModal from './EditDeviceModal';
import AddDeviceModal from './AddDeviceModal';
import DeviceConfigDisplay from './DeviceConfigDisplay';
import DeviceConfigHistory from './DeviceConfigHistory';

const DevicesPage: React.FC = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<{ [deviceId: string]: string }>({});
  const [selectedDeviceForHistory, setSelectedDeviceForHistory] = useState<string | null>(null);

  useEffect(() => {
    loadDevices();
  }, []);

  const loadDevices = async () => {
    try {
      setLoading(true);
      const devicesData = await traqPackAPI.getDevices();
      setDevices(devicesData);
    } catch (error) {
      console.error('Error loading devices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditDevice = (device: Device) => {
    setSelectedDevice(device);
    setEditModalOpen(true);
  };

  const handleSaveDevice = async (deviceId: string, newName: string) => {
    await traqPackAPI.updateDevice(deviceId, newName);
    await loadDevices();
  };

  const handleDeleteDevice = async (deviceId: string) => {
    // TODO: Implement delete functionality in API
    console.log('Delete device:', deviceId);
    setDeleteConfirmId(null);
    await loadDevices();
  };

  const filteredDevices = devices.filter(device => {
    const matchesSearch = 
      device.device_nickname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.device_id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = 
      filterStatus === 'all' ||
      (filterStatus === 'active' && device.is_active) ||
      (filterStatus === 'inactive' && !device.is_active);
    
    return matchesSearch && matchesStatus;
  });

  const getTimeSinceLastSeen = (lastSeen?: string) => {
    if (!lastSeen) return 'Never';
    
    const now = new Date();
    const lastSeenDate = new Date(lastSeen);
    const diffMs = now.getTime() - lastSeenDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffMins > 0) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  const handleCategoryChange = (deviceId: string, category: string) => {
    setSelectedCategory(prev => ({
      ...prev,
      [deviceId]: category
    }));
  };

  const configCategories = [
    { value: 'all', label: 'All Settings' },
    { value: 'network', label: 'Network' },
    { value: 'tracking', label: 'Tracking' },
    { value: 'motion', label: 'Motion' },
    { value: 'environment', label: 'Environment' },
    { value: 'safety', label: 'Safety' },
    { value: 'device', label: 'Device Info' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-traqpack-yellow border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-900">Device Management</h1>
          <div className="flex gap-2">
            <button
              onClick={() => {
                const url = new URL(window.location.href);
                url.searchParams.set('newDeviceManager', 'true');
                // Keep the current tab
                url.searchParams.set('tab', 'devices');
                window.location.href = url.toString();
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
            >
              <Sparkles className="w-5 h-5" />
              <span>Try New Manager</span>
            </button>
            <button
              onClick={() => setAddModalOpen(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-traqpack-yellow rounded-lg hover:bg-traqpack-yellow-dark transition-colors duration-200 text-black font-medium"
            >
              <Plus className="w-5 h-5" />
              <span>Add Device</span>
            </button>
          </div>
        </div>
        
        {/* Search and Filter Controls */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search devices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-traqpack-yellow focus:border-transparent outline-none"
            />
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                filterStatus === 'all'
                  ? 'bg-traqpack-yellow text-black'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilterStatus('active')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                filterStatus === 'active'
                  ? 'bg-traqpack-yellow text-black'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Active
            </button>
            <button
              onClick={() => setFilterStatus('inactive')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                filterStatus === 'inactive'
                  ? 'bg-traqpack-yellow text-black'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Inactive
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mt-8">
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <Signal className="w-6 h-6 text-green-600" />
              <span className="text-2xl font-bold text-gray-900">{devices.filter(d => d.is_active).length}</span>
            </div>
            <p className="text-gray-700 font-medium">Active Devices</p>
            <p className="text-xs text-gray-500 mt-1">Last 4 hours</p>
          </div>
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <Signal className="w-6 h-6 text-red-600" />
              <span className="text-2xl font-bold text-gray-900">{devices.filter(d => !d.is_active).length}</span>
            </div>
            <p className="text-gray-700 font-medium">Inactive Devices</p>
          </div>
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
              <span className="text-2xl font-bold text-gray-900">{devices.filter(d => 
                d.temperature_enabled || d.humidity_enabled || d.light_enabled || 
                d.g_sensor_enabled || d.geofence_enabled || d.battery_low_threshold
              ).length}</span>
            </div>
            <p className="text-gray-700 font-medium">Monitored</p>
            <p className="text-xs text-gray-500 mt-1">With sensors enabled</p>
          </div>
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <Smartphone className="w-6 h-6 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">{devices.length}</span>
            </div>
            <p className="text-gray-700 font-medium">Total Devices</p>
          </div>
        </div>
      </div>

      {/* Devices Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredDevices.map((device: any) => {
          const currentCategory = selectedCategory[device.id] || 'all';
          return (
            <div
              key={device.id}
              className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden border border-gray-200"
            >
              {/* Device Header */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-traqpack-yellow/10 rounded-lg flex items-center justify-center">
                      <Smartphone className="w-5 h-5 text-traqpack-yellow-dark" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{device.device_nickname || 'Unnamed Device'}</h3>
                      <p className="text-sm text-gray-500">IMEI: {device.device_id}</p>
                      {device.firmware_version && (
                        <p className="text-xs text-gray-400 font-mono">
                          FW: {device.firmware_version.includes('V') 
                            ? device.firmware_version.split('V')[1].split(':')[0] 
                            : device.firmware_version.slice(-8)}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    device.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {device.is_active ? 'Active' : 'Inactive'}
                  </div>
                </div>

                {/* Category Selector */}
                <div className="mb-4">
                  <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Configuration</label>
                  <select
                    value={currentCategory}
                    onChange={(e) => handleCategoryChange(device.id, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-traqpack-yellow focus:border-transparent text-sm"
                  >
                    {configCategories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                {/* Configuration Display */}
                <DeviceConfigDisplay device={device} category={currentCategory} />

                {/* Last Update */}
                <div className="flex items-center space-x-2 text-sm text-gray-600 mt-3">
                  <Clock className="w-4 h-4" />
                  <span>Last seen: {getTimeSinceLastSeen(device.last_seen)}</span>
                </div>
              </div>

              {/* Device Actions */}
              <div className="p-4 bg-gray-50 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => handleEditDevice(device)}
                    className="flex items-center space-x-2 px-4 py-2 bg-white rounded-lg hover:bg-gray-100 transition-colors duration-200 border border-gray-300"
                  >
                    <Edit2 className="w-4 h-4" />
                    <span className="font-medium">Edit</span>
                  </button>
                  {deleteConfirmId === device.id ? (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleDeleteDevice(device.id)}
                        className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200 text-sm font-medium"
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => setDeleteConfirmId(null)}
                        className="px-3 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors duration-200 text-sm font-medium"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirmId(device.id)}
                      className="flex items-center space-x-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span className="font-medium">Delete</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredDevices.length === 0 && (
        <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-200">
          <Smartphone className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No devices found</h3>
          <p className="text-gray-500">Try adjusting your search or filters</p>
        </div>
      )}

      {/* Edit Modal */}
      <EditDeviceModal
        device={selectedDevice}
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedDevice(null);
        }}
        onSave={handleSaveDevice}
      />

      {/* Add Device Modal */}
      <AddDeviceModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onDeviceAdded={() => {
          loadDevices();
          setAddModalOpen(false);
        }}
      />

      {/* Device Configuration History Section */}
      <div className="mt-8">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Configuration History Log</h2>
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Filter by device:</span>
                <select
                  value={selectedDeviceForHistory || ''}
                  onChange={(e) => setSelectedDeviceForHistory(e.target.value || null)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-traqpack-yellow focus:border-transparent outline-none text-sm"
                >
                  <option value="">All Devices</option>
                  {devices.map((device) => (
                    <option key={device.device_id} value={device.device_id}>
                      {device.device_nickname || device.device_id}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        </div>
        <DeviceConfigHistory deviceId={selectedDeviceForHistory} />
      </div>
    </div>
  );
};

export default DevicesPage;